import type { DynamicClient } from '@dynamic-labs-sdk/client';
import { InvalidParamError } from '@dynamic-labs-sdk/client';
import {
  type TransferAmountParams,
  parseAmountToSmallestUnit,
} from '@dynamic-labs-sdk/client/core';

import { isBitcoinWalletAccount } from '../../isBitcoinWalletAccount';
import { sendBitcoin } from '../../sendBitcoin';

const BTC_DECIMALS = 8;

/**
 * Transfers native BTC from one wallet to another.
 *
 * Converts the human-readable BTC amount string to satoshis (8 decimals)
 * and delegates to `sendBitcoin`.
 *
 * @param params.amount - The amount in BTC as a decimal string (e.g. "0.001").
 * @param params.recipient - The recipient Bitcoin address.
 * @param params.walletAccount - The wallet account to send from.
 * @param client - The Dynamic client instance.
 * @returns An object containing the transaction hash.
 */
export const bitcoinTransferAmount = async (
  { amount, recipient, walletAccount }: TransferAmountParams,
  client: DynamicClient
): Promise<{ transactionHash: string }> => {
  if (!isBitcoinWalletAccount(walletAccount)) {
    throw new InvalidParamError(
      'Wallet account is not a Bitcoin wallet account'
    );
  }

  const satoshis = parseAmountToSmallestUnit({ amount, decimals: BTC_DECIMALS });

  const result = await sendBitcoin(
    {
      transaction: {
        amount: satoshis,
        recipientAddress: recipient,
      },
      walletAccount,
    },
    client
  );

  return { transactionHash: result.transactionId };
};
